import { Box, FormControl, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';
import { COUNTRIES } from 'utils/countries.utils';
import CountryRiskLevel, {
  TCountryRiskLevel,
} from 'DesignSystem/DataDisplay/CountryRiskLevel/CountryRiskLevel';
import FilterAutocompleteSelection, {
  IFilterAutocompleteSelectItem,
} from 'Components/Filter/FilterAutocompleteSelection';
import React, { FC, useMemo, useCallback } from 'react';

export interface ICountryOption {
  code: string;
  label: string;
  riskLevel?: TCountryRiskLevel;
  isInEU?: boolean;
}

interface ICountryMultiselectAutocompleteProps {
  value?: string[]; // Array of country codes
  placeholder?: string;
  size?: 'small' | 'medium';
  hasError?: boolean;
  showCountryRisk?: boolean;
  errorMessage?: string;
  helperText?: string;
  maxSelections?: number;
  euCountriesOnly?: boolean;
  onCountrySelect: (countryCodes: string[]) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
  '&.MuiFormHelperText-root': {
    marginTop: theme.spacing(0.5),
  },
}));

const CountryMultiselectAutocomplete: FC<ICountryMultiselectAutocompleteProps> = ({
  value = [],
  placeholder = 'Select countries',
  hasError,
  errorMessage = 'Please select at least one country',
  helperText,
  size = 'medium',
  showCountryRisk,
  maxSelections,
  euCountriesOnly,
  disabled,
  onCountrySelect,
  'data-testid': testId,
}) => {
  const selectedCountries = useMemo(() => {
    return value.map(code => code.toUpperCase());
  }, [value]);

  // Convert countries to FilterAutocompleteSelection items
  const countryItems: IFilterAutocompleteSelectItem[] = useMemo(() => {
    const filtered = euCountriesOnly ? COUNTRIES.filter(c => c.isInEU) : COUNTRIES;
    return filtered.map(country => ({
      id: country.code,
      title: country.label,
      endAdornment:
        showCountryRisk && country.riskLevel ? (
          <Box ml={1}>
            <CountryRiskLevel riskLevel={country.riskLevel} />
          </Box>
        ) : undefined,
    }));
  }, [euCountriesOnly, showCountryRisk]);

  const handleSelectionChange = useCallback(
    (selectedIds: string[] | string | undefined) => {
      const ids = Array.isArray(selectedIds) ? selectedIds : selectedIds ? [selectedIds] : [];

      // Check max selections limit
      if (maxSelections && ids.length > maxSelections) {
        return;
      }

      onCountrySelect(ids);
    },
    [onCountrySelect, maxSelections]
  );

  const isSelectionLimitReached = useMemo(
    () => (maxSelections ? value.length >= maxSelections : false),
    [maxSelections, value.length]
  );

  const displayPlaceholder = useMemo(() => {
    if (value.length > 0) return '';
    if (isSelectionLimitReached) return `Maximum ${maxSelections} countries selected`;
    return placeholder;
  }, [value.length, isSelectionLimitReached, maxSelections, placeholder]);

  return (
    <FormControl fullWidth size={size} error={hasError}>
      <FilterAutocompleteSelection
        items={countryItems}
        selected={selectedCountries}
        multiple
        size={size}
        placeholder={displayPlaceholder}
        hasError={hasError}
        errorMessage={errorMessage}
        data-testid={testId}
        onSelectionChange={handleSelectionChange}
        itemName="country"
        disabled={disabled}
      />

      {!hasError && helperText && (
        <StyledFormHelperText data-testid={`${testId}-helper`}>{helperText}</StyledFormHelperText>
      )}
    </FormControl>
  );
};

export default CountryMultiselectAutocomplete;
